import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { morphSection } from '@theme/section-renderer';
import { CartUpdateEvent, ThemeEvents } from '@theme/events';

/** Time until which to ignore checkbox change (prevents double add when restore/sync set .checked) */
let ignoreCheckboxChangeUntil = 0;

/**
 * Component for managing additional services checkboxes in cart
 */
class CartAdditionalServices extends Component {
  /** @type {Map<string, boolean>} */
  #checkboxStates = new Map();

  /** @type {MutationObserver | null} */
  #mutationObserver = null;

  /** Prevents re-entry and double add when restore/sync fires change */
  #handlingCheckboxChange = false;

  connectedCallback() {
    super.connectedCallback();
    this.#initCheckboxes();
    this.#loadProductPricesFromDataAttributes();

    // Start observing checkbox changes to prevent morph from resetting them
    this.#startObservingCheckboxes();

    // When cart is updated (e.g. main product removed), clear additional services if no main products left
    document.addEventListener(ThemeEvents.cartUpdate, this.#onCartUpdateClearAdditionalIfEmpty);

    // After our restore (post-morph), accept checkbox states so our MutationObserver does not overwrite them
    document.addEventListener('cart-additional-services:apply-states', this.#onApplyCheckboxStates);

    // Delay sync to ensure cart is loaded
    setTimeout(() => {
      this.#syncCheckboxesWithCart();
    }, 100);

    // Also sync after a longer delay to catch any late morph operations
    setTimeout(() => {
      this.#syncCheckboxesWithCart();
    }, 1000);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(ThemeEvents.cartUpdate, this.#onCartUpdateClearAdditionalIfEmpty);
    document.removeEventListener('cart-additional-services:apply-states', this.#onApplyCheckboxStates);
    if (this.#mutationObserver) {
      this.#mutationObserver.disconnect();
      this.#mutationObserver = null;
    }
  }

  /** Empty cart drawer inner HTML (content of cart-items-component) — show immediately when only additional products left */
  static #EMPTY_CART_DRAWER_INNER = `
<div class="cart-drawer__header">
  <button ref="closeButton" on:click="cart-drawer-component/close" class="button close-button cart-drawer__close-button button-unstyled" aria-label="Close dialog">
    <span class="svg-wrapper icon-caret icon-caret--backward"><svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M11 5.5L7 9.5L3 5.5" stroke="currentColor" stroke-width="var(--icon-stroke-width)" stroke-linecap="round" stroke-linejoin="round"></path></svg></span>
    Back
  </button>
</div>
<div class="cart-drawer__content motion-reduce" aria-label="Cart">
  <span class="cart-drawer__heading h3 cart-drawer__heading--empty">Your cart is empty</span>
  <div class="cart-drawer__items">
    <div class="cart-items__wrapper">
      <a class="button cart-items__empty-button" href="/collections/all">Continue shopping</a>
    </div>
  </div>
</div>`;

  /**
   * When cart is updated by another component: if no main products remain, show empty cart
   * immediately and remove additional products in the background.
   * @param {CartUpdateEvent} event - The cart update event
   */
  #onCartUpdateClearAdditionalIfEmpty = async (event) => {
    if (event?.detail?.data?.source === 'cart-additional-services') return;

    const cart = await this.#fetchCart();
    if (!cart?.items?.length) return;

    const additionalHandles = this.#getAdditionalServiceHandlesFromDocument();
    if (additionalHandles.size === 0) return;

    const mainItems = cart.items.filter((item) => {
      const handle = this.#getProductHandleFromItem(item);
      return handle && !additionalHandles.has(handle);
    });

    if (mainItems.length > 0) return;

    // Only additional products left: show empty cart immediately, then remove them in background.
    const toRemove = cart.items
      .filter((item) => {
        const handle = this.#getProductHandleFromItem(item);
        return handle && additionalHandles.has(handle) && (item.key || item.id);
      })
      .map((item) => item.key || item.id?.toString());

    const sectionId = this.#showEmptyCartDrawer();
    if (sectionId && toRemove.length > 0) {
      this.#removeAdditionalProductsInBackground(toRemove, sectionId);
    }
  };

  /**
   * Replace drawer content with empty cart UI. Returns sectionId for later morph, or null.
   * @returns {string | null} Section ID of the drawer's cart-items-component
   */
  #showEmptyCartDrawer() {
    const drawerComponent =
      document.querySelector('cart-drawer-component dialog[open] cart-items-component') ||
      document.querySelector('cart-drawer-component cart-items-component');
    if (!drawerComponent || !(drawerComponent instanceof HTMLElement) || !drawerComponent.dataset.sectionId)
      return null;
    const sectionId = drawerComponent.dataset.sectionId;
    drawerComponent.innerHTML = CartAdditionalServices.#EMPTY_CART_DRAWER_INNER;

    // Remove donut mask from header cart icon immediately (so it stays removed during background removal).
    CartAdditionalServices.#applyEmptyCartIconState();

    document.dispatchEvent(
      new CartUpdateEvent({}, sectionId, {
        itemCount: 0,
        source: 'cart-additional-services',
      })
    );
    return sectionId;
  }

  /**
   * Apply empty state to all cart icons in the document (remove SVG mask, hide bubble).
   * Called when we show empty drawer so the icon is not clipped during background removal.
   */
  static #applyEmptyCartIconState() {
    document.querySelectorAll('.header-actions__cart-icon').forEach((el) => {
      el.classList.remove('header-actions__cart-icon--has-cart');
      const bubble = el.querySelector('.cart-bubble');
      const count = el.querySelector('.cart-bubble__text-count');
      if (bubble) {
        bubble.classList.add('visually-hidden');
        bubble.classList.remove('cart-bubble--animating');
      }
      if (count) count.classList.add('hidden');
    });
  }

  /**
   * Remove additional products via API in background; when done, only update header (total, count),
   * do not morph the drawer so the empty state we showed stays and there is no jump.
   * @param {string[]} toRemove - Line item keys or ids to remove
   * @param {string} sectionId - Section ID for final event
   */
  async #removeAdditionalProductsInBackground(toRemove, sectionId) {
    for (const itemKeyOrId of toRemove) {
      await this.#removeCartItemById(itemKeyOrId, true);
    }
    await this.#updateCartHeaderOnly(sectionId);
  }

  /**
   * Update only header (cart total, bubble count) and dispatch event; do not morph the drawer.
   * Used after we have already shown empty drawer and removed additional products in background.
   * @param {string} sectionId - Section ID for the cart update event
   */
  async #updateCartHeaderOnly(sectionId) {
    const cart = await this.#fetchCart();
    if (cart) {
      await this.#updateCartTotal(cart);
      document.dispatchEvent(
        new CartUpdateEvent({}, sectionId, {
          itemCount: cart.items?.length ?? 0,
          source: 'cart-additional-services',
        })
      );
    }
  }

  /**
   * Get set of additional service product handles from checkboxes (from document so it works
   * after morphSection replaces the section).
   * @returns {Set<string>}
   */
  #getAdditionalServiceHandlesFromDocument() {
    const handles = new Set();
    const root = document.querySelector('cart-additional-services');
    if (!root) return handles;
    root.querySelectorAll('.cart-service-checkbox').forEach((checkbox) => {
      if (checkbox instanceof HTMLElement && checkbox.dataset.productHandle) {
        handles.add(checkbox.dataset.productHandle);
      }
    });
    return handles;
  }

  /**
   * Remove cart item by line key or id (for clearing additional services without checkbox ref)
   * @param {string} itemKeyOrId - Cart item key (preferred) or id
   * @param {boolean} [skipDelay] - If true, do not wait after response (faster when removing multiple)
   */
  async #removeCartItemById(itemKeyOrId, skipDelay = false) {
    if (!itemKeyOrId) return;
    const formData = this.#createCartFormData(itemKeyOrId, '0');
    const response = await fetch(Theme.routes.cart_change_url, {
      ...fetchConfig('javascript', { body: formData }),
    });
    await this.#handleCartResponse(response, 'remove item from cart', { skipDelay });
  }

  /**
   * Save checkbox states to internal map
   */
  #saveCheckboxStates() {
    this.#getCheckboxes().forEach((checkbox) => {
      if (checkbox instanceof HTMLInputElement) {
        this.#checkboxStates.set(checkbox.id, checkbox.checked);
      }
    });
  }

  /**
   * Apply checkbox states from event (after another instance did restore post-morph).
   * Stops the observer from overwriting the restored state.
   * @param {CustomEvent<{ states: Record<string, boolean> }>} event
   */
  #onApplyCheckboxStates = (event) => {
    const states = event.detail?.states;
    if (!states || typeof states !== 'object') return;
    Object.entries(states).forEach(([id, checked]) => {
      this.#checkboxStates.set(id, !!checked);
    });
  };

  /**
   * Start observing checkbox changes to prevent morph from resetting them
   */
  #startObservingCheckboxes() {
    // Save initial states
    this.#saveCheckboxStates();

    // Observe changes to checkboxes
    const mutationObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'checked') {
          const target = mutation.target;
          if (target instanceof HTMLInputElement && target.classList.contains('cart-service-checkbox')) {
            const expectedState = this.#checkboxStates.get(target.id);
            if (expectedState !== undefined && target.checked !== expectedState) {
              // Restore state after a small delay to let morph finish
              setTimeout(() => {
                if (target.isConnected) {
                  target.checked = expectedState;
                }
              }, 10);
            }
          }
        }
      });
    });

    this.#mutationObserver = mutationObserver;

    // Observe all checkboxes
    this.#getCheckboxes().forEach((checkbox) => {
      if (checkbox instanceof HTMLInputElement) {
        mutationObserver.observe(checkbox, {
          attributes: true,
          attributeFilter: ['checked']
        });
      }
    });
  }

  /**
   * Get all service checkboxes
   * @returns {NodeListOf<Element>} Checkboxes
   */
  #getCheckboxes() {
    return this.querySelectorAll('.cart-service-checkbox');
  }

  /**
   * Initialize checkboxes and their event listeners
   */
  #initCheckboxes() {
    this.#getCheckboxes().forEach((checkbox) => {
      if (checkbox instanceof HTMLInputElement) {
        checkbox.addEventListener('change', (event) => {
          this.#handleCheckboxChange(checkbox, event);
        });
      }
    });
  }

  /**
   * Load product prices from data attributes (set by Liquid)
   */
  #loadProductPricesFromDataAttributes() {
    for (const checkbox of this.#getCheckboxes()) {
      if (!(checkbox instanceof HTMLElement)) continue;

      const variantId = checkbox.dataset.variantId;
      const price = checkbox.dataset.productPrice;
      const serviceType = checkbox.dataset.productHandle;

      if (!serviceType) continue;

      // If variant ID is already set from Liquid, format and display price
      if (variantId && price) {
        const priceElement = this.querySelector(`[data-service-price="${serviceType}"]`);
        if (priceElement instanceof HTMLElement) {
          priceElement.textContent = `$${parseFloat(price).toFixed(2)}`;
        }
      } else {
        // Fallback: try to fetch from API (may not work for unlisted products)
        this.#loadProductPriceFromAPI(checkbox, serviceType);
      }
    }
  }

  /**
   * Fallback: Load product price from API
   * @param {HTMLElement} checkbox - The checkbox element
   * @param {string} serviceType - The service type (product handle)
   */
  async #loadProductPriceFromAPI(checkbox, serviceType) {
    if (!(checkbox instanceof HTMLElement)) return;

    const productUrl = checkbox.dataset.productUrl;
    if (!productUrl || !serviceType) return;

    try {
      // Fetch product data from Shopify
      const response = await fetch(`${productUrl}.json`);
      if (!response.ok) {
        console.warn(`Product ${serviceType} not accessible via API (may be unlisted)`);
        return;
      }

      const data = await response.json();

      if (data.product && data.product.variants && data.product.variants.length > 0) {
        const variant = data.product.variants[0];
        const priceElement = this.querySelector(`[data-service-price="${serviceType}"]`);

        if (priceElement instanceof HTMLElement) {
          // Format price
          const price = (variant.price / 100).toFixed(2);
          priceElement.textContent = `$${price}`;
          checkbox.dataset.variantId = variant.id;
          checkbox.dataset.productId = data.product.id;
        }
      }
    } catch (error) {
      console.error('Error loading product price from API:', error);
    }
  }

  /**
   * Handle checkbox change event
   * @param {HTMLInputElement} checkbox - The checkbox element
   * @param {Event} [event] - The change event (used to ignore programmatic changes)
   */
  async #handleCheckboxChange(checkbox, event) {
    if (event && !event.isTrusted) return;
    if (this.#handlingCheckboxChange) return;
    // Ignore changes that happen right after we set .checked in restore/sync (new component still reacts)
    if (ignoreCheckboxChangeUntil > Date.now()) return;

    const variantId = checkbox.dataset.variantId;
    const productHandle = checkbox.dataset.productHandle;

    if (!variantId || !productHandle) {
      console.error('[CartAdditionalServices] Variant ID or product handle not found');
      return;
    }

    const isChecked = checkbox.checked;
    const previousState = !isChecked;

    this.#handlingCheckboxChange = true;
    try {
      if (isChecked) {
        await this.#addProductToCart(variantId);
      } else {
        await this.#removeProductFromCart(productHandle);
      }

      await this.#updateCart();
    } catch (error) {
      console.error('[CartAdditionalServices] Error updating cart:', error);
      checkbox.checked = previousState;
    } finally {
      this.#handlingCheckboxChange = false;
    }
  }

  /**
   * Fetch cart data
   * @returns {Promise<{ items?: Array<any>; total_price?: number }>} Cart data
   */
  async #fetchCart() {
    const response = await fetch(Theme.routes.cart_url + '.js');
    return response.json();
  }

  /**
   * Get product handle from item (/cart.js can return handle or product_handle)
   * @param {{ handle?: string; product_handle?: string; product_url?: string }} item - Cart item
   * @returns {string | null} Product handle
   */
  #getProductHandleFromItem(item) {
    return item.handle || item.product_handle || item.product_url?.split('/products/')[1]?.split('?')[0] || null;
  }

  /**
   * Check if product exists in cart
   * @param {{ items?: Array<{ variant_id?: number; product_handle?: string; product_url?: string }> }} cart - Cart data
   * @param {string} variantId - Variant ID to check
   * @param {string} productHandle - Product handle to check
   * @returns {boolean} True if product exists in cart
   */
  #isProductInCart(cart, variantId, productHandle) {
    if (!cart.items) return false;
    return cart.items.some((/** @type {{ variant_id?: number; product_handle?: string; product_url?: string }} */ item) => {
      if (item.variant_id?.toString() === variantId.toString()) {
        return true;
      }
      const itemHandle = this.#getProductHandleFromItem(item);
      return itemHandle === productHandle;
    });
  }

  /**
   * Restore checkbox states after morphSection
   */
  async #restoreCheckboxStates() {
    ignoreCheckboxChangeUntil = Date.now() + 400;
    for (let attempt = 0; attempt < 5; attempt++) {
      await new Promise((resolve) => setTimeout(resolve, 50));

      let allRestored = true;
      this.#checkboxStates.forEach((checked, checkboxId) => {
        const checkbox = document.getElementById(checkboxId);
        if (checkbox instanceof HTMLInputElement) {
          if (checkbox.checked !== checked) {
            checkbox.checked = checked;
            allRestored = false;
          }
        } else {
          allRestored = false;
        }
      });

      if (allRestored) break;
    }
  }

  /**
   * Find cart items component
   * @returns {HTMLElement | null} Cart items component or null
   */
  #findCartItemsComponent() {
    return (
      document.querySelector('cart-drawer-component dialog[open] cart-items-component') ||
      document.querySelector('cart-drawer-component cart-items-component') ||
      document.querySelector('main cart-items-component')
    );
  }

  /**
   * Create FormData for cart operation
   * @param {string} itemId - Item ID
   * @param {string} quantity - Quantity
   * @returns {FormData} FormData instance
   */
  #createCartFormData(itemId, quantity) {
    const formData = new FormData();
    formData.append('id', itemId);
    formData.append('quantity', quantity);

    const cartItemsComponent = this.#findCartItemsComponent();
    const sectionId = cartItemsComponent?.dataset.sectionId;
    const sectionKey = this.#getSectionKey(sectionId || '');

    if (sectionKey) {
      formData.append('sections', sectionKey);
    }

    return formData;
  }

  /**
   * Handle cart API response
   * @param {Response} response - API response
   * @param {string} operation - Operation name for error messages
   * @param {{ skipDelay?: boolean }} [options] - If skipDelay true, do not wait after response
   */
  async #handleCartResponse(response, operation, options = {}) {
    if (!response.ok) {
      const errorText = await response.text();
      console.error(`[CartAdditionalServices] Failed to ${operation}, response:`, response);
      console.error(`[CartAdditionalServices] Error response text:`, errorText);
      throw new Error(`Failed to ${operation}`);
    }
    await response.json();
    if (!options.skipDelay) {
      await new Promise((resolve) => setTimeout(resolve, 200));
    }
  }

  /**
   * Add product to cart
   * @param {string} variantId - The variant ID
   */
  async #addProductToCart(variantId) {
    const checkbox = this.querySelector(`[data-variant-id="${variantId}"]`);
    if (!(checkbox instanceof HTMLElement)) {
      console.error('[CartAdditionalServices] Checkbox not found for variant:', variantId);
      return;
    }

    const productHandle = checkbox.dataset.productHandle;
    if (!productHandle) {
      console.error('[CartAdditionalServices] Product handle not found');
      return;
    }

    const cart = await this.#fetchCart();
    if (this.#isProductInCart(cart, variantId || '', productHandle)) {
      return; // Product already in cart
    }

    const formData = this.#createCartFormData(variantId, '1');
    const response = await fetch(Theme.routes.cart_add_url, {
      ...fetchConfig('javascript', { body: formData }),
    });

    await this.#handleCartResponse(response, 'add product to cart');
  }

  /**
   * Find cart item by variant ID or product handle
   * @param {{ items?: Array<{ id?: number; variant_id?: number; product_handle?: string; product_url?: string }> }} cart - Cart data
   * @param {string | null} variantId - Variant ID to find
   * @param {string} productHandle - Product handle to find
   * @returns {{ id?: number; variant_id?: number; product_handle?: string; product_url?: string } | null} Cart item or null
   */
  #findCartItem(cart, variantId, productHandle) {
    if (!cart.items) return null;
    return cart.items.find((/** @type {{ variant_id?: number; product_handle?: string; product_url?: string }} */ item) => {
      if (variantId && item.variant_id?.toString() === variantId.toString()) {
        return true;
      }
      const itemHandle = this.#getProductHandleFromItem(item);
      return itemHandle === productHandle;
    }) || null;
  }

  /**
   * Remove product from cart by handle
   * @param {string} productHandle - The product handle
   */
  async #removeProductFromCart(productHandle) {
    const cart = await this.#fetchCart();

    const checkbox = this.querySelector(`[data-product-handle="${productHandle}"]`);
    const variantId = checkbox instanceof HTMLElement ? checkbox.dataset.variantId : null;
    const itemToRemove = this.#findCartItem(cart, variantId || null, productHandle);

    if (!itemToRemove?.id) {
      return; // Item not in cart
    }

    const formData = this.#createCartFormData(itemToRemove.id.toString(), '0');
    const response = await fetch(Theme.routes.cart_change_url, {
      ...fetchConfig('javascript', { body: formData }),
    });

    await this.#handleCartResponse(response, 'remove product from cart');
  }

  /**
   * Update cart UI after changes
   */
  async #updateCart() {
    const cartItemsComponent = this.#findCartItemsComponent();
    const sectionId = cartItemsComponent?.dataset.sectionId;

    if (!sectionId) {
      console.warn('[CartAdditionalServices] Section ID not found');
      return;
    }

    await this.#updateCartWithSectionId(sectionId);
  }

  /**
   * Get cart item count from section HTML or cart data
   * @param {string | undefined} sectionHTML - Section HTML string
   * @param {{ items?: Array<any> } | undefined} cartData - Cart data
   * @returns {number} Item count
   */
  #getItemCount(sectionHTML, cartData) {
    if (sectionHTML) {
      const newSectionHTML = new DOMParser().parseFromString(sectionHTML, 'text/html');
      const countText = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
      if (countText) {
        return parseInt(countText, 10);
      }
    }
    return cartData?.items?.length || 0;
  }

  /**
   * Get section key for Shopify Sections API
   * @param {string} sectionId - The section ID
   * @returns {string | null} Section key or null if header section
   */
  #getSectionKey(sectionId) {
    return sectionId.includes('header') ? null : `shopify-section-${sectionId}`;
  }

  /**
   * Update cart UI with specific section ID
   * @param {string} sectionId - The section ID
   */
  async #updateCartWithSectionId(sectionId) {
    const sectionKey = this.#getSectionKey(sectionId);

    // If header section, just update without morphing
    if (!sectionKey) {
      const data = await this.#fetchCart();

      if (data) {
        await this.#updateCartTotal(data);
        await this.#syncCheckboxesWithCart();

        const itemCount = data.items ? data.items.length : 0;
        document.dispatchEvent(
          new CartUpdateEvent({}, sectionId, {
            itemCount: itemCount,
            source: 'cart-additional-services',
          })
        );
      }
      return;
    }

    // Get cart data with sections
    const response = await fetch(
      `${Theme.routes.cart_url}.js?${new URLSearchParams({
        sections: sectionKey,
      })}`
    );

    const data = await response.json();

    // Show shimmer on cart total before update
    const cartTotalElement = document.querySelector('text-component[ref="cartTotal"]');
    if (cartTotalElement && 'shimmer' in cartTotalElement && typeof cartTotalElement.shimmer === 'function') {
      cartTotalElement.shimmer();
    }

    // Save checkbox states before morphSection (it will reset them)
    this.#saveCheckboxStates();

    // Update section - morphSection will update both cart-products and cart-summary
    if (data.sections?.[sectionKey]) {
      morphSection(sectionId, data.sections[sectionKey]);
      await this.#restoreCheckboxStates();
      // Tell the new component (in DOM after morph) to use these states so its MutationObserver does not overwrite
      document.dispatchEvent(
        new CustomEvent('cart-additional-services:apply-states', {
          detail: { states: Object.fromEntries(this.#checkboxStates) },
        })
      );
    } else {
      console.warn('[CartAdditionalServices] Section data not found for key:', sectionKey);
    }

    // Brief wait for morphSection to complete
    await new Promise((resolve) => setTimeout(resolve, 80));

    // Fetch fresh cart data and update UI
    const freshCartData = await this.#fetchCart();

    if (freshCartData) {
      await this.#updateCartTotal(freshCartData);
      await this.#syncCheckboxesWithCartData(freshCartData);
    }

    // Get cart item count from section or fresh cart data
    const itemCount = this.#getItemCount(data.sections?.[sectionKey], freshCartData);

    // Dispatch cart update event
    const cartUpdateEvent = new CartUpdateEvent({}, sectionId, {
      itemCount: itemCount,
      sections: data.sections,
      source: 'cart-additional-services',
    });

    document.dispatchEvent(cartUpdateEvent);

    const cartItemsComponentForEvent = document.querySelector(
      `cart-items-component[data-section-id="${sectionId}"]`
    );
    cartItemsComponentForEvent?.dispatchEvent(cartUpdateEvent);
  }

  /**
   * Format price to match theme (money_without_trailing_zeros) so total doesn't flash zeros.
   * @param {number} totalPrice - Total price in cents
   * @returns {string} Formatted price
   */
  #formatPrice(totalPrice) {
    const currencyCodeEnabled = document.querySelector('[data-cart-subtotal]') !== null;
    const amount = totalPrice / 100;
    let formatted;

    if (currencyCodeEnabled) {
      formatted = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
      }).format(amount);
    } else {
      formatted = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(amount);
    }
    // Match theme: strip trailing zeros (e.g. $2,541.00 → $2,541)
    return formatted.replace(/\.?0+$/, '');
  }

  /**
   * Update cart total element
   * @param {{ total_price?: number }} cart - Cart data
   */
  async #updateCartTotal(cart) {
    if (!cart) return;

    const totalPrice = cart.total_price ?? 0;

    // Try multiple times to find the element after morphSection
    let cartTotalElement = null;
    for (let i = 0; i < 5; i++) {
      cartTotalElement = document.querySelector('text-component[ref="cartTotal"]');
      if (cartTotalElement) break;
      await new Promise((resolve) => setTimeout(resolve, 50));
    }

    if (!cartTotalElement) {
      console.warn('[CartAdditionalServices] Cart total element not found');
      return;
    }

    const formattedPrice = this.#formatPrice(totalPrice);
    cartTotalElement.textContent = formattedPrice;
    cartTotalElement.setAttribute('value', formattedPrice.replace(/[^0-9.]/g, ''));

    if (cartTotalElement.innerHTML !== formattedPrice) {
      cartTotalElement.innerHTML = formattedPrice;
    }

    cartTotalElement.removeAttribute('shimmer');
  }

  /**
   * Sync checkboxes with current cart state
   */
  async #syncCheckboxesWithCart() {
    try {
      const cart = await this.#fetchCart();
      await this.#syncCheckboxesWithCartData(cart);
    } catch (error) {
      console.error('[CartAdditionalServices] Error syncing checkboxes with cart:', error);
    }
  }

  /**
   * Sync checkboxes with provided cart data
   * @param {{ items?: Array<{ product_url?: string; variant_id?: number; product_handle?: string; id?: number }> }} cart - Cart data
   */
  async #syncCheckboxesWithCartData(cart) {
    try {
      if (!cart?.items) {
        return;
      }
      // Do not set ignoreCheckboxChangeUntil here: delayed sync (100ms/1s) would block real user clicks
      // Use a more reliable selector - try both this component and document
      let checkboxes = this.#getCheckboxes();
      if (checkboxes.length === 0) {
        checkboxes = document.querySelectorAll('cart-additional-services .cart-service-checkbox');
      }

      checkboxes.forEach((checkbox) => {
        if (!(checkbox instanceof HTMLInputElement)) return;

        const productHandle = checkbox.dataset.productHandle;
        if (!productHandle) return;

        const checkboxVariantId = checkbox.dataset.variantId || null;
        const itemInCart = this.#findCartItem(cart, checkboxVariantId, productHandle);
        const shouldBeChecked = !!itemInCart;

        checkbox.checked = shouldBeChecked;
        this.#checkboxStates.set(checkbox.id, shouldBeChecked);

        // Ensure variant ID is set if product is in cart
        if (itemInCart?.variant_id && !checkbox.dataset.variantId) {
          checkbox.dataset.variantId = itemInCart.variant_id.toString();
        }
      });
    } catch (error) {
      console.error('[CartAdditionalServices] Error syncing checkboxes with cart:', error);
    }
  }
}

if (!customElements.get('cart-additional-services')) {
  customElements.define('cart-additional-services', CartAdditionalServices);
}
